/* eslint-disable jsx-a11y/anchor-is-valid */
import type { MutableRefObject } from 'react';
import React, { useEffect } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';
import type { ParsedUrlQuery } from 'querystring';

import { ArrowLeftIcon, ArrowRightIcon } from '@motorway/mw-highway-code';

import PaginationLink from 'Components/Pagination/PaginationLink';
import { scrollIntoView } from 'Utilities/helpers';

import { getCircles, resolveQueryParamsWithPage } from './Pagination.helper';
import PaginationTexts from './Pagination.texts.json';
import type { BasicPaginationMeta } from './Pagination.types';

import styles from './Pagination.module.scss';

const LocalT = new MDText(PaginationTexts);

interface PaginationProps {
	analyticsEventHandler?: (page: number) => void;
	meta: BasicPaginationMeta;
	otherQueryParams?: ParsedUrlQuery;
	pathName: string;
	scrollToRef?: MutableRefObject<JSX.Element>;
}

const Pagination: React.FC<PaginationProps> = ({
	analyticsEventHandler,
	meta,
	otherQueryParams,
	pathName,
	scrollToRef,
}) => {
	const { itemsPerPage, pageNumber, pages, totalItemsCount } = meta;

	useEffect(() => {
		const scrollToTarget = scrollToRef?.current || document.body;
		// @ts-expect-error scrollToTarget JSX.Element does not have getBoundingClientRect method
		scrollIntoView(scrollToTarget, { behavior: 'smooth' });
	}, [scrollToRef, pageNumber]);

	const circles = getCircles({ itemsPerPage, pageNumber, totalItemsCount });
	const start = (pageNumber - 1) * itemsPerPage + 1;
	const end = Math.min(pageNumber * itemsPerPage, totalItemsCount);

	if (!totalItemsCount || !pages || !itemsPerPage) {
		return null;
	}

	return (
		<div className={styles.pagination}>
			<PaginationLink pathName={pathName} query={resolveQueryParamsWithPage(pageNumber - 1, otherQueryParams)}>
				<a
					aria-label={
						pageNumber === 1
							? `${LocalT.translate('firstPage')}`
							: `${LocalT.translate('goToPreviousPage', { number: pageNumber - 1 })}`
					}
					className={cx(styles.controlButton, { [styles.disabled]: pageNumber === 1 })}
					onClick={() => analyticsEventHandler?.(pageNumber - 1)}
				>
					<ArrowLeftIcon />
				</a>
			</PaginationLink>
			<nav
				aria-label={`${LocalT.translate('paginationNavigation')}`}
				className={styles.paginationWrapper}
				role="navigation"
			>
				<ul>
					{circles?.map((item) => {
						const isActivePage = pageNumber === item;

						return (
							<li key={item}>
								<PaginationLink pathName={pathName} query={resolveQueryParamsWithPage(item, otherQueryParams)}>
									<a
										aria-current={isActivePage}
										aria-label={
											isActivePage
												? `${LocalT.translate('currentPage', { number: item })}`
												: `${LocalT.translate('goToPage', { number: item })}`
										}
										className={cx(styles.pageButton, { [styles.active]: isActivePage })}
										onClick={() => analyticsEventHandler?.(item)}
									>
										{item}
									</a>
								</PaginationLink>
							</li>
						);
					})}
				</ul>
				<div className={styles.resultCount}>{`${start} - ${end} of ${totalItemsCount} `}</div>
			</nav>
			<PaginationLink pathName={pathName} query={resolveQueryParamsWithPage(pageNumber + 1, otherQueryParams)}>
				<a
					aria-label={
						pageNumber === pages
							? `${LocalT.translate('lastPage')}`
							: `${LocalT.translate('goToNextPage', { number: pageNumber + 1 })}`
					}
					className={cx(styles.controlButton, { [styles.disabled]: pageNumber === pages })}
					onClick={() => analyticsEventHandler?.(pageNumber + 1)}
				>
					<ArrowRightIcon />
				</a>
			</PaginationLink>
		</div>
	);
};

export default Pagination;
